import type {
  CloudFunction,
  CloudFunctionResult,
  FunctionsService,
} from "@infrastructure/interfaces/database.interface";
import logger from "@logger";
import { httpsCallable } from "firebase/functions";
import type { z } from "zod";
import { auth, functions } from "../config/firebase";
import { parseCallableUsersListResponse } from "./parseCallableUsersListResponse";

function chatIdForPair(uid1: string, uid2: string): string {
  return [uid1, uid2].sort().join("_");
}

function isCloudFunctionsDisabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_DISABLE_CLOUD_FUNCTIONS === "true";
}

function stubEventId(data: unknown): string {
  return (data as { eventId?: string }).eventId ?? "stub-event";
}

function stubEventMetrics(eventId: string) {
  return {
    eventId,
    currentParticipants: 0,
    cumulativeEntrants: 0,
    paidUnlocks: 0,
    activeWindowParticipants: 0,
    matchCount: 0,
    genderBalance: { female: 0, male: 0, nonbinary: 0, unknown: 0 },
    onboardingIncomplete: 0,
    boostedParticipants: 0,
    completeProfiles: 0,
    unlockRate: 0,
    matchRate: 0,
  };
}

function stubSafetyAlert(data: unknown, status: string) {
  return {
    id: (data as { alertId?: string }).alertId ?? "stub-alert",
    uid: "stub-user",
    displayName: "Stub",
    phoneNumber: null,
    photoUrl: null,
    eventId: null,
    eventDisplayName: null,
    message: null,
    location: null,
    status,
    createdAt: new Date().toISOString(),
    acknowledgedAt: null as string | null,
    resolvedAt: null as string | null,
    sms: { recipientCount: 0, usedFallback: true },
  };
}

function stubHandleLike(data: unknown) {
  if (import.meta.env.VITE_STUB_HANDLE_LIKE_MUTUAL === "true") {
    const likedUserId = (data as { likedUserId?: unknown }).likedUserId;
    const uid = auth.currentUser?.uid;
    if (typeof likedUserId === "string" && uid && likedUserId !== uid) {
      return { mutual: true, chatId: chatIdForPair(uid, likedUserId) };
    }
  }
  return { mutual: false };
}

function stubEventUnlock(data: unknown) {
  return { success: true, eventId: stubEventId(data), alreadyUnlocked: false };
}

const stubHandlers: Record<string, (data: unknown) => unknown> = {
  handleLike: stubHandleLike,
  setCurrentEventContext: stubEventUnlock,
  unlockEventAccess: stubEventUnlock,
  adminListEvents: () => ({ events: [] }),
  adminCreateEvent: (data) => ({ success: true, eventId: stubEventId(data) }),
  adminGetEventStats: (data) => ({
    event: {
      eventId: stubEventId(data),
      displayName: "Stub Event",
      status: "active",
      createdAt: null,
      safetyAlertRecipients: [],
    },
    stats: stubEventMetrics(stubEventId(data)),
  }),
  adminSaveEventNightSnapshot: (data) => ({
    snapshotId: "stub-snap",
    snapshot: {
      id: "stub-snap",
      eventId: stubEventId(data),
      eventDisplayName: "Stub Event",
      label: null,
      capturedAt: new Date().toISOString(),
      metrics: stubEventMetrics(stubEventId(data)),
    },
  }),
  adminListEventNightSnapshots: () => ({ snapshots: [] }),
  adminGetWeeklyEngagement: () => ({ weeks: [] }),
  adminListOrphanedUsers: () => ({ users: [], scannedCount: 0, scanLimit: 200, hasMore: false }),
  adminAssignUserToEvent: (data) => ({ eventId: stubEventId(data), updated: true }),
  requestSafetyHelp: () => ({ alertId: "stub-alert", usedFallback: true, recipientCount: 1 }),
  requestAccountDeletion: () => ({ requested: true, alreadyRequested: false }),
  adminListAccountDeletionRequests: () => ({ requests: [], scannedCount: 0, scanLimit: 200, hasMore: false }),
  adminUpdateEventSafetyRecipients: (data) => ({
    success: true,
    eventId: stubEventId(data),
    recipients: (data as { recipients?: unknown }).recipients ?? [],
  }),
  adminListSafetyAlerts: () => ({ alerts: [] }),
  adminGetSafetyAlert: (data) => ({ alert: stubSafetyAlert(data, "open"), liveUser: null }),
  adminUpdateSafetyAlertStatus: (data) => {
    const status = (data as { status?: string }).status ?? "acknowledged";
    return {
      alert: {
        ...stubSafetyAlert(data, status),
        acknowledgedAt: new Date().toISOString(),
        resolvedAt: status === "resolved" ? new Date().toISOString() : null,
      },
    };
  },
  processMatch: () => ({ success: false }),
  sendPushNotification: () => ({ success: false }),
  updateUserPreferences: () => ({ success: false }),
  generateUserRecommendations: () => ({ userIds: [] }),
  reportUser: () => ({ success: false, reportId: "stub" }),
  getDiscoveryUsers: () => ({ users: [] }),
  getLikedYouUsers: () => ({ users: [] }),
  getPublicUserProfiles: () => ({ users: [] }),
};

function stubCallableResult<R>(functionName: string, data: unknown): R {
  logger.warn("callable skipped (VITE_DISABLE_CLOUD_FUNCTIONS=true)", { functionName });
  return (stubHandlers[functionName]?.(data) ?? {}) as R;
}

export class FirebaseFunctionsService implements FunctionsService {
  // Generic function caller
  callFunction<T = unknown, R = unknown>(functionName: string): CloudFunction<T, R> {
    if (isCloudFunctionsDisabled()) {
      return async (data: T) => stubCallableResult<R>(functionName, data);
    }
    const callable = httpsCallable<T, R>(functions, functionName);
    return async (data: T): Promise<R> => {
      const result = await callable(data);
      return result.data;
    };
  }

  // Call function with data
  async callFunctionWithData<T = unknown, R = unknown>(functionName: string, data: T): Promise<CloudFunctionResult<R>> {
    try {
      const callable = this.callFunction<T, R>(functionName);
      const result = await callable(data);
      return { data: result };
    } catch (error) {
      logger.error(`Error calling function ${functionName}:`, error, {
        cause: error instanceof Error ? error.cause : undefined,
      });
      throw error;
    }
  }

  /**
   * Call a CF that returns `{ users: Row[] }`, deriving the row type from Zod.
   * Fails the call on a bad envelope; drops invalid rows and logs a warning.
   */
  async callFunctionWithUsersList<TReq, TRow>(
    functionName: string,
    data: TReq,
    rowSchema: z.ZodType<TRow>
  ): Promise<TRow[]> {
    const result = await this.callFunctionWithData<TReq, unknown>(functionName, data);
    try {
      return parseCallableUsersListResponse(result.data, functionName, rowSchema);
    } catch (error) {
      logger.error(`Error parsing ${functionName} response:`, error, {
        cause: error instanceof Error ? error.cause : undefined,
      });
      throw error;
    }
  }

  // Process match between users
  async processMatch(userId1: string, userId2: string): Promise<{ success: boolean; matchId?: string }> {
    try {
      const result = await this.callFunctionWithData("processMatch", { userId1, userId2 });
      return result.data as { success: boolean; matchId?: string };
    } catch (error) {
      logger.error("Error processing match:", error);
      throw error;
    }
  }

  // Send push notification
  async sendPushNotification(
    userId: string,
    title: string,
    body: string,
    data?: Record<string, string>
  ): Promise<{ success: boolean }> {
    try {
      const result = await this.callFunctionWithData("sendPushNotification", {
        userId,
        title,
        body,
        data,
      });
      return result.data as { success: boolean };
    } catch (error) {
      logger.error("Error sending push notification:", error);
      throw error;
    }
  }

  // Update user preferences
  async updateUserPreferences(userId: string, preferences: Record<string, unknown>): Promise<{ success: boolean }> {
    try {
      const result = await this.callFunctionWithData("updateUserPreferences", {
        userId,
        preferences,
      });
      return result.data as { success: boolean };
    } catch (error) {
      logger.error("Error updating user preferences:", error);
      throw error;
    }
  }

  // Generate user recommendations
  async generateUserRecommendations(userId: string, limit: number = 20): Promise<{ userIds: string[] }> {
    try {
      const result = await this.callFunctionWithData("generateUserRecommendations", {
        userId,
        limit,
      });
      return result.data as { userIds: string[] };
    } catch (error) {
      logger.error("Error generating user recommendations:", error);
      throw error;
    }
  }

  // Report user
  async reportUser(
    reporterId: string,
    reportedUserId: string,
    reason: string,
    details?: string
  ): Promise<{ success: boolean; reportId: string }> {
    try {
      const result = await this.callFunctionWithData("reportUser", {
        reporterId,
        reportedUserId,
        reason,
        details,
      });
      return result.data as { success: boolean; reportId: string };
    } catch (error) {
      logger.error("Error reporting user:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const firebaseFunctionsService = new FirebaseFunctionsService();
export default firebaseFunctionsService;
